import { useEffect, useState } from 'react';
import { Outlet, createFileRoute, useNavigate, useRouterState, useSearch } from '@tanstack/react-router';
import { useSetAtom } from 'jotai';
import { Action, Page } from '@/../../server/types/loggingTypes';
import logo from '@/assets/logo.png';
import splashImage from '@/assets/splash_image.png';
import LoginWithEmail from '@/components/LoginWithEmail';
import PhoneVerification, { PhoneVerificationContext } from '@/components/PhoneVerification';
import Button from '@/components/lib/Button';
import { useAppSelector } from '@/store/hooks';
import { inviteCodeAtom } from '@/store/inviteAtom';
import { getStatsigClient } from '@/utils/statsig';

type SignupSearch = {
  enter_from?: string;
  email?: string;
  onboardingRewardUniqueId?: string;
  code?: string;
};

export const Route = createFileRoute('/signup')({
  validateSearch: (search): SignupSearch => {
    const otpEmailFields =
      search.enter_from === 'otp_email' && search.email
        ? {
            enter_from: search.enter_from,
            email: search.email as string,
          }
        : {};
    return {
      ...otpEmailFields,
      onboardingRewardUniqueId: search.onboardingRewardUniqueId as string | undefined,
      code: search.code as string | undefined,
    };
  },
  component: SignupRouteComponent,
});

export enum LoginMethod {
  EMAIL = 'email',
  PHONE = 'phone',
}

export default function SignupRouteComponent() {
  // Determine if we're at the base /signup path
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const isRootPath = pathname === '/signup' || pathname === '/signup/';

  const authState = useAppSelector((state) => state.auth);
  const navigate = useNavigate();

  const [loginMethod, setLoginMethod] = useState<LoginMethod>(LoginMethod.PHONE);

  const { enter_from, email, onboardingRewardUniqueId, code } = useSearch({ from: '/signup' });

  const setInviteCode = useSetAtom(inviteCodeAtom);

  useEffect(() => {
    if (code) {
      setInviteCode(code.toString());
    }

    if (authState.isAuthenticated) {
      navigate({ to: '/' });
    }

    if (enter_from === 'otp_email') {
      setLoginMethod(LoginMethod.EMAIL);
    }
  }, [authState.isAuthenticated, navigate, enter_from]);

  useEffect(() => {
    getStatsigClient().logEvent(Page.LANDING_PAGE, Action.VIEW);
  }, []);

  if (!isRootPath) {
    // Render nested routes (phone/email signup or verification) instead of the landing page
    return <Outlet />;
  }

  return (
    <div className="flex flex-col items-center min-h-screen bg-[#121212]">
      {/* Header */}
      <div className="relative flex flex-row items-center justify-center md:justify-start py-3 px-4 md:px-12 w-full md:pt-12 md:pb-12">
        <img src={logo} alt="Bond Logo" className="h-8 md:h-10 object-contain" />
      </div>

      {/* Main Content */}
      <div className="flex flex-col md:flex-row items-center py-0 gap-0 w-full md:justify-center md:flex-1 md:items-center">
        {/* Left Side (Image & Heading) - visible on small screens */}
        <h1 className="text-white text-header-2 text-center max-w-[350px] mb-2 mt-6 block md:hidden">
          Your best life starts with a question
        </h1>
        <div className="w-full md:basis-1/2 shrink grow-0 flex justify-center md:justify-end mb-8 md:mb-0 md:pr-[40px]">
          <img
            src={splashImage}
            alt="Landing Page Visual"
            className="w-[300px] h-auto md:w-full md:max-w-[400px] object-contain"
          />
        </div>

        {/* Continue button (mobile) navigates to phone sign-up route */}
        <div className="w-full px-6 mb-2 block md:hidden">
          <Button
            size="lg"
            onClick={() =>
              navigate({
                to: '/signup/phone-signup',
                search: {
                  onboardingRewardUniqueId,
                },
              })
            }
          >
            Continue
          </Button>
        </div>

        {/* Right Side (Login) */}
        <div className="basis-1/2 grow pl-[40px] flex-col justify-center hidden md:flex">
          <div className="flex flex-col items-center max-w-[334px]">
            <h1 className="text-white text-header-2 text-center max-w-[350px]">
              Your best life starts with a question
            </h1>

            {loginMethod === LoginMethod.EMAIL ? (
              <LoginWithEmail
                onboardingRewardUniqueId={onboardingRewardUniqueId}
                setLoginMethod={setLoginMethod}
                enterFrom={enter_from}
                otpEmailAddress={email}
              />
            ) : (
              <PhoneVerification
                onboardingRewardUniqueId={onboardingRewardUniqueId}
                setLoginMethod={setLoginMethod}
                context={PhoneVerificationContext.LandingPage}
              />
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="text-center text-[#ffffff40] text-[14px]/[20px] max-w-[250px] pb-6 pt-4">
        <p>By tapping Continue you accept our</p>
        <a
          href="https://docs.google.com/document/d/1zImTFVA7nfo3-ZcbCk6Hr1Y67W5P9GHEhi8PPaUAxdc/edit?usp=sharing"
          className="hover:underline no-underline text-[#ffffff40] font-semibold"
        >
          Privacy Policy
        </a>
        <span> & </span>
        <a
          href="https://docs.google.com/document/d/1p3-fJ25CafAI13qyOYEZMu4rCH95JVaXRfsNejTyDD0/edit?usp=sharing"
          className="hover:underline no-underline text-[#ffffff40] font-semibold"
        >
          Terms of Service
        </a>
      </div>
    </div>
  );
}
